import * as React from 'react'
// libs
import { useRouter } from 'hooks/useRouter'
import { useSelector } from 'react-redux'
import { useMutation, useQuery } from '@apollo/client/react'
// context
import { TableManagementContext } from 'containers/Search/layers/BL'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// constants
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// graphql
import { FILTER_SIDEBAR_QUERY } from 'graphql/filter/queries'
import { PLAN_ACTION_LIST_QUERY } from 'graphql/planActions/queries'
import { PLAN_ITEM_CREATE_MUTATION } from 'graphql/plan/mutations'
import { PLAN_ACTION_CREATE_MUTATION } from 'graphql/planActions/mutations'
// types
import { ConfigItem } from 'layouts/types'
import { TRefetch } from 'types/refetch'
import { MutationFn } from 'types/apollo'
import { NCreatePlanItem } from 'types/plan/helpers'
import { FilterTarget, IField, IPlanAction } from 'types/graphql'
import { NCreatePlanAction, NGetPlanActions } from 'types/actions/helpers'
// utils
import get from 'lodash/get'
import find from 'lodash/fp/find'
import pipe from 'lodash/fp/pipe'
import getFp from 'lodash/fp/get'
import { safeGetOr } from 'utils/fp'
import { getFieldsList } from 'utils/fields'
import { getSidebarConfig } from 'utils/layouts'

type Props = React.PropsWithChildren<unknown>

export type TActionCreate = MutationFn<NCreatePlanAction.IResponse, NCreatePlanAction.IVariables>
export type TPlanItemCreate = MutationFn<NCreatePlanItem.IResponse, NCreatePlanItem.IVariables>

interface IContext {
  data: IPlanAction[]
  fields: IField[]
  config: ConfigItem[]
  refetch: TRefetch<NGetPlanActions.IResponse, NGetPlanActions.IVariables>
  isLoading: boolean
  totalCount: number
  pageFilter: string
  actionCreate: TActionCreate
  isRefetching: boolean
  planItemCreate: TPlanItemCreate
}

export const ActionsPageContext = React.createContext<IContext>({} as any)

const ActionsPageAPI: React.FC<Props> = ({ children }) => {
  const router = useRouter()

  const querySlug = router.query.slug?.[0]

  const { search, filter, page, perPage } = React.useContext(TableManagementContext)

  const { data: filtersQuery, loading } = useQuery(FILTER_SIDEBAR_QUERY, {
    variables: {
      filter: {
        target: FilterTarget.Action
      }
    }
  })

  const objectEntities = useSelector(objectEntitiesSelector)

  const { data, networkStatus, refetch } = useQuery(PLAN_ACTION_LIST_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      filter: {
        ...filter,
        description: search,
        filterSlug: querySlug
      },
      pagination: {
        pageSkip: page * perPage,
        pageTake: perPage
      }
    }
  })

  const [actionCreate] = useMutation(PLAN_ACTION_CREATE_MUTATION)
  const [planItemCreate] = useMutation(PLAN_ITEM_CREATE_MUTATION)

  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.ACTION)
  const filters = safeGetOr<any, any, any[]>([], 'filters.data')(filtersQuery)
  const config = getSidebarConfig(filters, 'actions')

  const pageFilter = React.useMemo(() => {
    if (querySlug) {
      return pipe(
        find(({ slug }) => querySlug === slug),
        getFp('name')
      )(filters)
    }
  }, [filters, querySlug])

  const actions: IPlanAction[] = get(data, 'planActions.data', [])
  const totalCount: number = get(data, 'planActions.pagination.totalCount', 0)

  React.useEffect(() => {
    if (filters.length && !pageFilter) {
      router.push(`/actions/${filters[0].slug}`)
    }
  }, [filters, pageFilter])

  const value = {
    config,
    fields,
    refetch,
    totalCount,
    pageFilter,
    actionCreate,
    planItemCreate,
    data: actions,
    isLoading: loading || networkStatus === 1,
    isRefetching: networkStatus === 2
  } as IContext

  return <ActionsPageContext.Provider value={value}>{children}</ActionsPageContext.Provider>
}

export default ActionsPageAPI
